import { AgGridReact } from 'ag-grid-react';
import {
  CellClassParams,
  CellClickedEvent,
  ColDef,
  EditableCallbackParams,
  ICellRendererParams,
  ITooltipParams,
  ValueFormatterParams,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import 'styles/ag-grid.css';
import { cn, getDynamicTableHeight } from 'utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DeleteIcon } from 'assets/icons/delete';
import {
  ECellValueDisplayMode,
  ECellValueType,
  EExcelTableColumnType,
  EExcelTablePaginationType,
  ExcelTableColDef,
  ExcelTableProps,
  GroupableRow,
  ValidationError,
  EValidationSeverity,
} from './types';
import { TaskIcon } from 'assets/icons/task';
import PlusIcon from 'assets/icons/plus';
import { ArrowDownIcon } from 'assets/icons/arrow-down';
import { numberFormatter, percentFormatter } from 'utils/ag-grid-formatter';
import { isEmpty, isNil, isNumber, omit } from 'lodash';
import { useTranslation } from 'react-i18next';
import { message, Pagination } from 'antd';
import { ConfirmationModal } from '../confirmation-modal';
import { ConfirmationModalType } from '../confirmation-modal/types';
import AgGridSelectRenderer from '../ag-grid-select-renderer';
import AgGridCheckboxRenderer from '../ag-grid-checkbox-renderer';
import AgGridDatePicker from '../ag-grid-date-picker';
import {
  theme,
  TOTAL_ROW_KEY,
  ADD_ROW_KEY,
  DEFAULT_ROW_KEY_COL_NAME,
  DELETE_ROW_KEY,
} from './constants';
import 'styles/ant-design.css';
import CustomBadge from '../custom-badge';
import AgGridLazySelectRenderer from '../ag-grid-lazy-select-renderer';

const ExcelTable = <T extends object>({
  data,
  tableContainerClassName,
  tableHeightBias,
  baseTableHeight,
  className,
  tableClassName,
  columnDefs,
  includeDeleteCol,
  includeAddRow,
  includeGroupAddRow,
  includeIdRow,
  summaryRowData,
  rowKeyColName = DEFAULT_ROW_KEY_COL_NAME,
  newRowData,
  paginationType,
  currentPage,
  totalRows,
  pageSize,
  pageSizeOptions,
  validateRow,
  onPageChange,
  onPageSizeChange,
  onDeleteRow,
  onNewRow,
  onDataChange,
  onErrorChange,
  groupConfig,
  onAddGroupRow,
  canDeleteRow,
  canAddRow,
  onRowClicked,
  enableErrorBorder,
  ...agGridReactProps
}: ExcelTableProps<T>) => {
  const [validationErrors, setValidationErrors] = useState<
    ValidationError<T>[]
  >([]);
  const [tableData, setTableData] = useState<T[]>([]);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<number | null>(null);

  const { t } = useTranslation();

  useEffect(() => {
    if (data) {
      setTableData(data);
    }
  }, [data]);

  const validateTableData = useCallback(
    (data?: T[]) => {
      const allErrors: ValidationError<T>[] = [
        ...(data || tableData).flatMap(
          (row, index) => validateRow?.(row, index, data || tableData, t) || []
        ),
      ];
      return {
        strictError: allErrors.some(
          (error) => error.severity === EValidationSeverity.STRICT
        ),
        softError: allErrors.some(
          (error) => error.severity === EValidationSeverity.SOFT
        ),
      };
    },
    [tableData, validateRow, t]
  );

  const validateRowData = useCallback(
    (row: T, rowIndex: number, data?: T[]) => {
      if (!validateRow)
        return {
          strictError: false,
          softError: false,
        };
      const rowErrors = validateRow(row, rowIndex, data || tableData, t);
      const strictErrors = rowErrors.filter(
        (error) =>
          !error.severity || error.severity === EValidationSeverity.STRICT
      );

      const softErrors = rowErrors.filter(
        (error) => error.severity === EValidationSeverity.SOFT
      );

      return {
        strictError: !isEmpty(strictErrors),
        softError: !isEmpty(softErrors),
      };
    },
    [tableData, validateRow, t]
  );

  const isInvalidSelection = useCallback(
    (value: unknown, col: ExcelTableColDef, data: unknown) => {
      if (
        col.columnType !== EExcelTableColumnType.SELECTION ||
        !col.selectionOptions
      )
        return false;
      const options =
        typeof col.selectionOptions !== 'function'
          ? col.selectionOptions
          : col.selectionOptions(data);

      const currentOption = options.find((option) => option.value === value);
      return !isNil(value) && !currentOption;
    },
    []
  );

  useEffect(() => {
    if (!validateRow) return;

    if (!isEmpty(tableData)) {
      const hasInvalidSelection = tableData.some((row) => {
        return Object.entries(row).some(([key, value]) => {
          const col = columnDefs.find((col) => col.field === key) as
            | ExcelTableColDef
            | undefined;

          if (!col) return false;
          return isInvalidSelection(value, col, row);
        });
      });

      const allErrors: ValidationError<T>[] = [
        ...tableData.flatMap((row, index) =>
          validateRow(row, index, tableData, t)
        ),
      ];

      const isStrictError = allErrors.filter(
        (error) =>
          !error.severity || error.severity === EValidationSeverity.STRICT
      );

      const isSoftError = allErrors.filter(
        (error) => error.severity === EValidationSeverity.SOFT
      );

      setValidationErrors(allErrors);
      onErrorChange?.({
        strict: isStrictError.length > 0 || hasInvalidSelection,
        soft: isSoftError.length > 0,
      });
    }
  }, [
    tableData,
    validateRow,
    onErrorChange,
    t,
    columnDefs,
    isInvalidSelection,
  ]);

  const onCellValueChanged = useCallback(
    (params: ValueSetterParams) => {
      const updatedData = tableData.map((row, index) => {
        if (index === params.node?.rowIndex) {
          return { ...row, [params.column.getColId()]: params.newValue };
        }
        return row;
      });
      setTableData(updatedData);

      if (onDataChange && params.node?.data && !isNil(params.node?.rowIndex)) {
        const errorValidation = validateRowData(
          params.node.data,
          params.node.rowIndex,
          updatedData
        );
        onDataChange(
          {
            data: params.node.data,
            isError: {
              strict: errorValidation.strictError,
              soft: errorValidation.softError,
            },
          },
          {
            data: updatedData,
            isError: {
              strict: errorValidation.strictError,
              soft: errorValidation.softError,
            },
          }
        );
      }
    },
    [tableData, onDataChange, validateRowData]
  );

  const isAddRow = useCallback((rowKey: unknown) => rowKey === ADD_ROW_KEY, []);

  // Grouping functionality
  const hasGroupConfig = !!groupConfig;

  const isGroupRow = useCallback((row: GroupableRow): row is GroupableRow => {
    return row !== null && typeof row === 'object' && !!row.isGroupRow;
  }, []);

  const isAddRowForGroup = useCallback(
    (row: GroupableRow): row is GroupableRow => {
      return row !== null && typeof row === 'object' && !!row.isAddRow;
    },
    []
  );

  const handleToggleGroup = useCallback(
    (groupId: string | number, event: React.MouseEvent) => {
      event.stopPropagation();
      if (groupConfig?.onToggleGroup) {
        groupConfig.onToggleGroup(groupId);
      }
    },
    [groupConfig]
  );

  const handleAddRowForGroup = useCallback(
    (groupId: string | number, event: React.MouseEvent) => {
      event.stopPropagation();
      if (onAddGroupRow) {
        onAddGroupRow(groupId, tableData);
      }
    },
    [onAddGroupRow, tableData]
  );

  const getGroupRowRenderer = useCallback(
    (params: ICellRendererParams) => {
      const row = params.data as GroupableRow;
      const groupId = row.groupId;
      const isExpanded = groupConfig?.expandedGroups?.[String(groupId)];

      if (!groupId) return null;

      return (
        <div
          className="flex items-center justify-center h-full cursor-pointer"
          onClick={(e) => handleToggleGroup(groupId, e)}
        >
          <div
            className={cn(
              'flex items-center justify-center w-10 h-10',
              isExpanded && 'rotate-180'
            )}
          >
            <ArrowDownIcon />
          </div>
        </div>
      );
    },
    [groupConfig, handleToggleGroup]
  );

  const getAddRowRenderer = useCallback(
    (params: ICellRendererParams) => {
      const row = params.data as GroupableRow;
      const groupId = row.groupId;

      if (!groupId) return null;

      return (
        <div
          className="flex items-center justify-center h-full cursor-pointer"
          onClick={(e) => handleAddRowForGroup(groupId, e)}
        >
          <div className="flex items-center justify-center w-10 h-10">
            <PlusIcon />
          </div>
        </div>
      );
    },
    [handleAddRowForGroup]
  );

  const getErrorStyle = useCallback(
    (
      field: keyof T,
      rowIndex: number,
      layerNumber: number | string,
      isInvalidSelection: boolean
    ) => {
      if (isAddRow(layerNumber)) return { backgroundColor: '#FFFFFF' };

      const hasError = validationErrors.some(
        (error) => error.field === field && error.rowIndex === rowIndex
      );

      // Different styling for soft vs. strict errors
      if (hasError) {
        const error = validationErrors.find(
          (error) => error.field === field && error.rowIndex === rowIndex
        );

        if (error?.severity === EValidationSeverity.SOFT) {
          return {
            backgroundColor: '#FFF7E3',
            ...(enableErrorBorder && { border: '1px solid #F59E0B' }),
          };
        }

        // Strict errors or undefined severity (backward compatibility)
        return {
          backgroundColor: '#FEE2E2',
          ...(enableErrorBorder && { border: '1px solid #EF4444' }),
        }; // Red for strict errors
      }

      return isInvalidSelection
        ? { backgroundColor: '#FEE2E2' }
        : { backgroundColor: '#FFFFFF' };
    },
    [isAddRow, validationErrors, enableErrorBorder]
  );

  const getErrorMessage = useCallback(
    (
      field: keyof T,
      rowIndex: number,
      layerNumber: number | undefined,
      isInvalidSelection: boolean
    ) => {
      if (isAddRow(layerNumber)) return '';

      const errors = validationErrors
        .filter((error) => error.field === field && error.rowIndex === rowIndex)
        .concat(
          isInvalidSelection
            ? [{ field, message: t('validation.validOption'), rowIndex }]
            : []
        );
      return errors.map((error) => error.message).join('. ');
    },
    [isAddRow, validationErrors, t]
  );

  const getCellRenderer = useCallback(
    (props: ICellRendererParams, col: ExcelTableColDef) => {
      if (props.data[rowKeyColName] === ADD_ROW_KEY) {
        return null;
      }

      if (props.data[rowKeyColName] === TOTAL_ROW_KEY) {
        if (col.type === ECellValueType.NUMERIC_COLUMN) {
          const formatter =
            col.displayMode === ECellValueDisplayMode.PERCENTAGE
              ? percentFormatter
              : numberFormatter;

          return formatter(
            { value: props.value } as ValueFormatterParams,
            Math.max(
              typeof col.precision === 'function'
                ? col.precision(props.data)
                : (col.precision ?? 2),
              0
            )
          );
        }
        return props.value;
      }

      if (
        col.columnType === EExcelTableColumnType.LAZY_SELECTION &&
        col.fetchOptions &&
        !props.data.isGroupRow &&
        !props.data.isAddRow
      ) {
        const disabled =
          typeof col.editable === 'function'
            ? !col.editable(props as EditableCallbackParams)
            : col.editable === false;
        return (
          <AgGridLazySelectRenderer
            node={props.node}
            columnId={props.column?.getColId()}
            value={props.value}
            initOptions={col.initOptions}
            fetchOptions={col.fetchOptions}
            disabled={disabled}
            defaultValue={col.defaultSelectionValue}
          />
        );
      }

      if (
        col.columnType === EExcelTableColumnType.SELECTION &&
        col.selectionOptions &&
        !props.data.isGroupRow &&
        !props.data.isAddRow
      ) {
        const disabled =
          typeof col.editable === 'function'
            ? !col.editable(props as EditableCallbackParams)
            : col.editable === false;
        return (
          <AgGridSelectRenderer
            node={props.node}
            columnId={props.column?.getColId()}
            value={props.value}
            data={props.data}
            options={col.selectionOptions}
            disabled={disabled}
            defaultValue={col.defaultSelectionValue}
          />
        );
      }

      if (
        col.columnType === EExcelTableColumnType.CHECKBOX &&
        !props.data.isGroupRow &&
        !props.data.isAddRow
      ) {
        const disabled =
          typeof col.editable === 'function'
            ? !col.editable(props as EditableCallbackParams)
            : col.editable === false;
        return (
          <AgGridCheckboxRenderer
            {...props}
            disabled={disabled}
            label={col.checkboxLabel}
          />
        );
      }

      if (col.columnType === EExcelTableColumnType.BADGE) {
        return (
          <div className="flex items-center justify-center w-full h-full p-0">
            <CustomBadge
              label={props.value}
              backgroundColor="bg-blue-100"
              textColor="text-blue-600"
            />
          </div>
        );
      }

      if (col.type === ECellValueType.NUMERIC_COLUMN) {
        const formatter =
          col.displayMode === ECellValueDisplayMode.PERCENTAGE
            ? percentFormatter
            : numberFormatter;

        return formatter(
          { value: props.value } as ValueFormatterParams,
          Math.max(
            typeof col.precision === 'function'
              ? col.precision(props.data)
              : (col.precision ?? 2),
            0
          )
        );
      }

      if (
        col.type === ECellValueType.DATE_COLUMN &&
        !props.data.isGroupRow &&
        !props.data.isAddRow
      ) {
        return (
          <AgGridDatePicker
            {...props}
            disabledDate={col.disabledDate}
            disabled={col.editable === false}
          />
        );
      }

      return props.value;
    },
    [rowKeyColName]
  );

  const createColumnDefs = useCallback(
    (columns: ExcelTableColDef[]): ExcelTableColDef[] => {
      const enhancedCols = columns.map((col) => ({
        ...omit(col, [
          'columnType',
          'selectionOptions',
          'checkboxLabel',
          'displayMode',
          'precision',
        ]),
        editable:
          col.editable !== undefined
            ? col.editable
            : (params: EditableCallbackParams) => {
                // Don't allow editing of the total row or group/add rows
                if (
                  params.data &&
                  (params.data[rowKeyColName] === TOTAL_ROW_KEY ||
                    isGroupRow(params.data) ||
                    isAddRowForGroup(params.data))
                ) {
                  return false;
                }
                return params.data[rowKeyColName] !== ADD_ROW_KEY;
              },
        cellStyle: (params: CellClassParams) => {
          const baseStyle = {
            ...(col.textAlign && { textAlign: col.textAlign }),
          };

          // Apply specific styling for badge columns
          if (col.columnType === EExcelTableColumnType.BADGE) {
            return {
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              ...baseStyle,
            };
          }

          // Apply special styling for the total row
          if (params.data && params.data[rowKeyColName] === TOTAL_ROW_KEY) {
            return {
              fontWeight: 'bold',
              backgroundColor: '#E3F2FB',
              ...baseStyle,
            };
          }

          // Apply the original cell style or error style
          if (col.cellStyle) {
            return typeof col.cellStyle === 'function'
              ? col.cellStyle(params)
              : col.cellStyle;
          }

          const errorStyle = getErrorStyle(
            col.field as keyof T,
            params.node?.rowIndex || 0,
            params.data[rowKeyColName],
            isInvalidSelection(params.value, col, params.data)
          );

          return {
            ...baseStyle,
            ...errorStyle,
          };
        },
        tooltipValueGetter: col.tooltipValueGetter
          ? col.tooltipValueGetter
          : (params: ITooltipParams) =>
              getErrorMessage(
                col.field as keyof T,
                params.node?.rowIndex || 0,
                params.data[rowKeyColName],
                isInvalidSelection(params.value, col, params.data)
              ),
        cellRenderer: (props: ICellRendererParams) => {
          if (col.cellRenderer) {
            return col.cellRenderer(props);
          }
          return getCellRenderer(props, col);
        },
      }));

      // If group config is provided, add actions column at the beginning
      if (hasGroupConfig) {
        enhancedCols.unshift({
          headerName: '',
          field: 'action',
          width: 50,
          minWidth: 50,
          flex: 0,
          sortable: false,
          filter: false,
          editable: false,
          cellStyle: () => ({ padding: 0 }),
          tooltipValueGetter: () => '',
          cellRenderer: (params: ICellRendererParams) => {
            if (isGroupRow(params.data)) {
              return getGroupRowRenderer(params);
            }
            if (isAddRowForGroup(params.data) && includeGroupAddRow) {
              return getAddRowRenderer(params);
            }
            return null;
          },
        });
      }

      return enhancedCols;
    },
    [
      getErrorStyle,
      getErrorMessage,
      getCellRenderer,
      rowKeyColName,
      isInvalidSelection,
      hasGroupConfig,
      isGroupRow,
      isAddRowForGroup,
      getGroupRowRenderer,
      getAddRowRenderer,
      includeGroupAddRow,
    ]
  );

  const enhanceColumnDefs = useMemo<ColDef[]>(() => {
    const colDefs = createColumnDefs(columnDefs);

    if (includeDeleteCol) {
      colDefs.push({
        field: DELETE_ROW_KEY,
        headerName: '',
        width: 46,
        maxWidth: 46,
        editable: false,
        cellStyle: (params: CellClassParams) => {
          if (
            isAddRow(params.data[rowKeyColName]) ||
            isGroupRow(params.data) ||
            isAddRowForGroup(params.data) ||
            (canDeleteRow && !canDeleteRow(params.data))
          ) {
            return { padding: 0, cursor: 'not-allowed', opacity: 0.5 };
          }
          return { padding: 0, cursor: 'pointer', opacity: 1 };
        },
        cellRenderer: (params: { data: { [key: string]: number } }) => {
          if (
            isAddRow(params.data[rowKeyColName]) ||
            isGroupRow(params.data) ||
            isAddRowForGroup(params.data)
          ) {
            return null;
          }

          const isDeleteEnabled = !canDeleteRow || canDeleteRow(params.data);

          return (
            <div
              className={`flex items-center justify-center w-full h-[46px] ${
                isDeleteEnabled
                  ? 'cursor-pointer text-red-500'
                  : 'cursor-not-allowed text-red-300'
              }`}
            >
              <DeleteIcon />
            </div>
          );
        },
      });
    }

    if (includeIdRow) {
      const idRow = {
        field: rowKeyColName,
        headerName: '',
        width: 80,
        resizable: false,
        headerComponent: () => (
          <div className="flex items-center justify-center w-full h-full">
            <TaskIcon />
          </div>
        ),
        valueGetter: (params: ValueGetterParams) => {
          if (isAddRow(params.data[rowKeyColName])) {
            return null;
          }
          if (params.data[rowKeyColName]) {
            return params.data[rowKeyColName];
          }
          return params.node?.rowIndex ? params.node.rowIndex + 1 : 1;
        },
        cellRenderer: (params: { value: string | number }) => {
          if (!params.value) {
            return (
              <div className="flex items-center justify-center w-full h-full cursor-pointer">
                <div className="flex items-center justify-center w-10 h-10">
                  <PlusIcon />
                </div>
              </div>
            );
          }
          return (
            <div className="flex items-center justify-center w-full h-full font-bold">
              {params.value}
            </div>
          );
        },
      };

      const existingIdRow =
        colDefs?.[0]?.children?.some(
          (child) => child.field === rowKeyColName
        ) || colDefs.some((col) => col.field === rowKeyColName);

      if (!existingIdRow) {
        if (colDefs?.[0]?.children?.length) {
          colDefs[0].children.unshift(idRow);
        } else {
          colDefs.unshift(idRow);
        }
      }
    }

    return colDefs;
  }, [
    columnDefs,
    createColumnDefs,
    isAddRowForGroup,
    isGroupRow,
    includeDeleteCol,
    includeIdRow,
    isAddRow,
    rowKeyColName,
    canDeleteRow,
  ]);

  const rowData = useMemo(() => {
    let rows = [...tableData];

    if (summaryRowData && tableData.length > 0) {
      const totalRow = summaryRowData;
      rows = [...rows, totalRow];
    }

    if (includeAddRow) {
      return [...rows, { [rowKeyColName]: ADD_ROW_KEY } as unknown as T];
    }

    return rows;
  }, [tableData, includeAddRow, rowKeyColName, summaryRowData]);

  const handleAddRow = useCallback(async () => {
    // Check if a row can be added using the canAddRow prop
    if (canAddRow) {
      const canAddResult = canAddRow(tableData);

      if (!canAddResult.canAdd) {
        const errorMessage =
          canAddResult.message || t('excelTable.errorBeforeAddRow');

        message.error(errorMessage);
        return;
      }
    }
    // Default validation if canAddRow is not provided
    else {
      const hasStrictErrors =
        validateRow &&
        tableData.some((row, index) => {
          const errors = validateRow(row, index, tableData, t);
          return errors.some(
            (error) =>
              !error.severity || error.severity === EValidationSeverity.STRICT
          );
        });

      if (hasStrictErrors && rowData.length > 1) {
        message.error(t('excelTable.errorBeforeAddRow'));
        return;
      }
    }

    const currentIndex = (
      tableData[tableData.length - 1] as Record<string, unknown>
    )?.[rowKeyColName];
    const newRow: T = {
      [rowKeyColName]: isNumber(currentIndex) ? currentIndex + 1 : 1,
      ...newRowData,
    } as T;
    setTableData([...tableData, newRow]);

    if (onNewRow) {
      const errorValidation = validateRowData(newRow, tableData.length);
      const tableErrorValidation = validateTableData();
      await onNewRow(
        {
          data: newRow,
          isError: {
            strict: errorValidation.strictError,
            soft: errorValidation.softError,
          },
        },
        {
          data: [...tableData, newRow],
          isError: {
            strict: tableErrorValidation.strictError,
            soft: tableErrorValidation.softError,
          },
        }
      );
    }
  }, [
    canAddRow,
    rowData.length,
    tableData,
    rowKeyColName,
    newRowData,
    onNewRow,
    t,
    validateRowData,
    validateTableData,
    validateRow,
  ]);

  const handleDeleteRow = useCallback(
    async (rowIndex: number) => {
      const updatedData = tableData.filter((_, index) => index !== rowIndex);
      const reorderedData = updatedData.map((row, index) => ({
        ...row,
        [rowKeyColName]: index + 1,
      }));
      setTableData(reorderedData);
      const errorValidation = validateRowData(
        tableData[rowIndex],
        rowIndex,
        reorderedData
      );
      const tableErrorValidation = validateTableData(reorderedData);
      if (onDeleteRow) {
        await onDeleteRow(
          {
            data: tableData[rowIndex],
            isError: {
              strict: errorValidation.strictError,
              soft: errorValidation.softError,
            },
          },
          {
            data: reorderedData,
            isError: {
              strict: tableErrorValidation.strictError,
              soft: tableErrorValidation.softError,
            },
          }
        );
      }
    },
    [tableData, onDeleteRow, rowKeyColName, validateRowData, validateTableData]
  );

  const onCellClicked = (params: CellClickedEvent) => {
    if (
      params.data[rowKeyColName] === ADD_ROW_KEY &&
      params.column.getColId() === rowKeyColName
    ) {
      handleAddRow();
    }

    if (
      params.data[rowKeyColName] !== ADD_ROW_KEY &&
      !isGroupRow(params.data) &&
      !isAddRowForGroup(params.data) &&
      params.column.getColId() === DELETE_ROW_KEY
    ) {
      // Check if the row can be deleted
      if (canDeleteRow && !canDeleteRow(params.data)) {
        return; // Skip deletion if canDeleteRow returns false
      }

      setRowToDelete(params.node?.rowIndex || 0);
      setShowDeleteConfirmation(true);
    }
  };

  const confirmDelete = useCallback(() => {
    if (rowToDelete !== null) {
      handleDeleteRow(rowToDelete);
      setShowDeleteConfirmation(false);
      setRowToDelete(null);
    }
  }, [rowToDelete, handleDeleteRow]);

  const renderPagination = useCallback(() => {
    if (paginationType === EExcelTablePaginationType.INFO) {
      const totalRow = tableData.length;
      return (
        <div className="text-sm text-gray-500 p-3 border-t border-gray-200 bg-white rounded-b-lg">
          {t('excelTable.paginationInfo', {
            from: tableData.length > 0 ? 1 : 0,
            to: totalRow,
            total: totalRow,
          })}
        </div>
      );
    }

    if (paginationType === EExcelTablePaginationType.DETAIL) {
      return (
        <Pagination
          className="custom-pagination mt-3"
          current={currentPage}
          total={totalRows}
          pageSize={pageSize}
          onShowSizeChange={onPageSizeChange}
          onChange={onPageChange}
          showSizeChanger
          pageSizeOptions={pageSizeOptions}
          showTotal={(total, range) =>
            t('general.paginationFooter', {
              from: range[0],
              to: range[1],
              total,
            })
          }
        />
      );
    }
  }, [
    currentPage,
    onPageChange,
    onPageSizeChange,
    pageSize,
    pageSizeOptions,
    paginationType,
    t,
    tableData.length,
    totalRows,
  ]);

  return (
    <div className={cn('ag-theme-alpine', className)}>
      <div
        className={tableContainerClassName}
        style={
          baseTableHeight
            ? {
                height: `${getDynamicTableHeight(
                  baseTableHeight,
                  tableHeightBias
                )}px`,
              }
            : undefined
        }
      >
        <AgGridReact
          theme={theme}
          rowData={rowData}
          columnDefs={enhanceColumnDefs}
          onRowClicked={onRowClicked}
          defaultColDef={{
            sortable: false,
            resizable: false,
            suppressMovable: true,
          }}
          getRowClass={(params) => {
            // Add hover class for all rows except add row and group rows
            if (
              params.data &&
              !isAddRow(params.data[rowKeyColName]) &&
              !isGroupRow(params.data) &&
              !isAddRowForGroup(params.data) &&
              !isNil(onRowClicked)
            ) {
              return 'excel-table-row-hover';
            }
            return '';
          }}
          onCellClicked={onCellClicked}
          onCellValueChanged={onCellValueChanged}
          suppressClickEdit={false}
          stopEditingWhenCellsLoseFocus={true}
          suppressDragLeaveHidesColumns={true}
          headerHeight={68}
          rowHeight={46}
          columnHoverHighlight={false}
          suppressRowHoverHighlight={true}
          enableCellTextSelection={true}
          animateRows={true}
          suppressScrollOnNewData={true}
          rowBuffer={10}
          suppressMovableColumns={true}
          suppressColumnMoveAnimation={true}
          tooltipShowDelay={0}
          tooltipHideDelay={10000}
          tooltipInteraction={true}
          className={tableClassName}
          {...agGridReactProps}
        />
      </div>

      {renderPagination()}

      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={confirmDelete}
        title={t('excelTable.deleteRow')}
        content={t('excelTable.deleteRowContent')}
        okText={t('general.yes')}
        cancelText={t('general.no')}
        type={ConfirmationModalType.DANGER}
      />
    </div>
  );
};

export default ExcelTable;
